import { authorizedRequest, HTTP_METHODS } from "utils/request";

export const STATUS = {
  DONE: "done",
  NOT_READY: "not_ready",
  CANCELLED: "cancelled",
};

export const ERRORS = {
  REQUEST_ERROR: "requestError",
  CANCELLED: STATUS.CANCELLED,
  LIMIT: "limit",
  EMPTY: "empty",
};

export const ERROR_CODES = {
  OK: 200,
  NO_RESULTS: 420, // Not time available for target date.
};

export default class Polling {
  isPolling = false;

  body = "";

  url = "";

  callbacks = [];

  wait = 1000;

  limit = 20;

  timeoutId = -1;

  count = 0;

  constructor(body = {}, url = "") {
    this.body = body;
    this.url = url;
  }

  registerCallback(callback) {
    this.callbacks.push(callback);
  }

  removeCallback(callback) {
    const index = this.callbacks.indexOf(callback);
    if (index !== -1) {
      this.callbacks.splice(index, 1);
    }
  }

  async start() {
    this.isPolling = true;
    this.loop();
    return new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  stop() {
    this.isPolling = false;
    if (this.timeoutId !== -1) {
      clearTimeout(this.timeoutId);
      this.timeoutId = -1;
    }
  }

  call(err, result) {
    // Make sure not to call after stop has been called.
    if (!this.isPolling) {
      return;
    }

    // Make sure the parameters are null if they weren't passed
    const normalResult = result || null;
    const normalErr = err || null;

    this.callbacks.forEach(callback => {
      callback(normalErr, normalResult);
    });

    if (err) {
      this.reject(err);
    } else {
      this.resolve(result);
    }

    this.stop();
  }

  processResponseStatus(response) {
    const { status } = response.additional_data;

    switch (status) {
      case STATUS.DONE:
        this.call(null, response);
        this.stop();
        break;
      case STATUS.CANCELLED:
      case null:
      case undefined:
        this.call(ERRORS.CANCELLED);
        this.stop();
        break;
      case STATUS.NOT_READY:
      default:
        // Increment count and stop if end is reached
        this.count++;
        if (this.count >= this.limit) {
          this.call(ERRORS.LIMIT);
          this.stop();
        } else {
          this.timeoutId = setTimeout(this.loop, this.wait);
        }
    }
  }

  processResponse(response) {
    let code = ERROR_CODES.OK;
    if (typeof response.code === "string") {
      code = Number.parseInt(response.code, 10);
    } else if (typeof response.code === "number") {
      code = response.code;
    }

    switch (code) {
      case ERROR_CODES.NO_RESULTS:
        this.call(ERRORS.EMPTY, null);
        this.stop();
        break;
      case ERROR_CODES.OK:
      default:
        this.processResponseStatus(response);
        break;
    }
  }

  async request() {
    try {
      // Poll
      const response = await authorizedRequest(this.url, {
        method: HTTP_METHODS.POST,
        body: this.body,
      });
      this.processResponse(response);
    } catch (e) {
      this.call(ERRORS.REQUEST_ERROR, e);
      this.stop();
    }
  }

  loop = () => {
    if (!this.isPolling) {
      return;
    }

    // Do the request and
    this.request();
  };
}
